import React, { useState, useRef } from 'react';
import { 
  MoreVertical, 
  Edit, 
  UserCheck, 
  UserX, 
  Trash2, 
  Mail, 
  Phone, 
  User,
  Info
} from 'lucide-react';
import { User as UserType } from '../../services/userManagementService';
import { useAppSelector } from '../../store/hooks';
import Dropdown from '../UI/Dropdown';
import { formatFullNameWithTitle } from '../../utils/guestNameUtils';

interface UserTableViewProps {
  users: UserType[];
  onEdit: (user: UserType) => void;
  onToggleStatus: (user: UserType) => void;
  onDelete: (user: UserType) => void;
  onShowDetails?: (user: UserType) => void;
  loading?: boolean;
}

const UserTableView: React.FC<UserTableViewProps> = ({
  users,
  onEdit,
  onToggleStatus,
  onDelete,
  onShowDetails,
  loading = false
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  
  // Create refs for each user's dropdown trigger
  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  
  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'host':
        return 'bg-blue-100 text-blue-800';
      case 'guest':
        return 'bg-green-100 text-green-800';
      case 'usher':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadgeColor = (isActive: boolean) => {
    return isActive 
      ? 'bg-green-100 text-green-800' 
      : 'bg-red-100 text-red-800';
  };

  if (loading) {
    return (
      <div className={`rounded-lg shadow ${
        theme === 'dark' 
          ? 'bg-gray-900/80 border border-gray-700/50' 
          : 'bg-white border border-gray-200'
      }`}>
        <div className={`px-4 sm:px-6 py-3 sm:py-4 border-b ${
          theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className={`animate-pulse h-5 sm:h-6 rounded w-24 sm:w-32 ${
            theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
          }`}></div>
        </div>
        <div className={`divide-y ${
          theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'
        }`}>
          {[...Array(5)].map((_, i) => (
            <div key={i} className="px-4 sm:px-6 py-3 sm:py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 sm:space-x-4">
                  <div className={`animate-pulse h-8 w-8 sm:h-10 sm:w-10 rounded-full ${
                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                  }`}></div>
                  <div className="space-y-2">
                    <div className={`animate-pulse h-3 sm:h-4 rounded w-24 sm:w-32 ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                    }`}></div>
                    <div className={`animate-pulse h-2 sm:h-3 rounded w-16 sm:w-24 ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                    }`}></div>
                  </div>
                </div>
                <div className={`animate-pulse h-6 w-6 sm:h-8 sm:w-8 rounded ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                }`}></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className={`rounded-lg shadow ${
        theme === 'dark' 
          ? 'bg-gray-900/80 border border-gray-700/50' 
          : 'bg-white border border-gray-200'
      }`}>
        <div className="px-4 sm:px-6 py-8 sm:py-12 text-center">
          <User className={`h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-3 sm:mb-4 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
          }`} />
          <h3 className={`text-base sm:text-lg font-medium mb-2 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>No users found</h3>
          <p className={`text-sm sm:text-base ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>
            Get started by creating a new user.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg shadow overflow-hidden ${
      theme === 'dark' 
        ? 'bg-gray-900/80 border border-gray-700/50' 
        : 'bg-white border border-gray-200'
    }`}>
      <div className="overflow-x-auto">
        <table className={`w-full divide-y ${
          theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'
        }`}>
          <thead className={theme === 'dark' ? 'bg-gray-800/50' : 'bg-gray-50'}>
            <tr>
              <th className={`px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-left text-xs font-medium uppercase tracking-wider ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
              }`}>
                User
              </th>
              <th className={`px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-left text-xs font-medium uppercase tracking-wider hidden sm:table-cell ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
              }`}>
                Contact
              </th>
              <th className={`px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-right text-xs font-medium uppercase tracking-wider whitespace-nowrap ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
              }`}>
                Status
              </th>
              <th className={`px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-left text-xs font-medium uppercase tracking-wider hidden lg:table-cell whitespace-nowrap ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
              }`}>
                Created
              </th>
              <th className="relative px-3 sm:px-4 lg:px-6 py-2 sm:py-3 w-12">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className={`divide-y ${
            theme === 'dark' ? 'bg-gray-900/80 divide-gray-700' : 'bg-white divide-gray-200'
          }`}>
            {users.map((user) => (
              <tr key={user.id} className={theme === 'dark' ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50'}>
                <td className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4">
                  <div className="flex items-center min-w-0">
                    <div className="flex-shrink-0 h-8 w-8 sm:h-10 sm:w-10">
                      <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="text-xs sm:text-sm font-medium text-primary-600">
                          {user.first_name.charAt(0)}{user.last_name.charAt(0)}
                        </span>
                      </div>
                    </div>
                    <div className="ml-2 sm:ml-4 min-w-0 flex-1 max-w-full">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                        <div className={`text-xs sm:text-sm font-medium truncate ${
                          theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                          {formatFullNameWithTitle(user.title, user.first_name, user.last_name)}
                        </div>
                        <div className="flex items-center flex-wrap gap-1">
                          {/* Display all roles from roles array, or fallback to primary role */}
                          {(user.roles && user.roles.length > 0 ? user.roles : [user.role]).map((role) => (
                            <span key={role} className={`inline-flex px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(role)}`}>
                              {role.charAt(0).toUpperCase() + role.slice(1)}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className={`text-xs sm:text-sm mt-1 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        <span className="hidden sm:inline">ID: </span>{user.id}
                      </div>
                      {/* Show contact info on mobile */}
                      <div className={`sm:hidden mt-1 text-xs ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        {user.email && (
                          <div className="flex items-center space-x-1 truncate">
                            <Mail className={`h-3 w-3 flex-shrink-0 ${
                              theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
                            }`} />
                            <span className="truncate">{user.email}</span>
                          </div>
                        )}
                        {user.phone && (
                          <div className="flex items-center space-x-1 truncate">
                            <Phone className={`h-3 w-3 flex-shrink-0 ${
                              theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
                            }`} />
                            <span className="truncate">{user.phone}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 whitespace-nowrap hidden sm:table-cell">
                  <div className={`text-xs sm:text-sm ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    {user.email && (
                      <div className="flex items-center space-x-1">
                        <Mail className={`h-3 w-3 ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
                        }`} />
                        <span className="truncate max-w-[200px]">{user.email}</span>
                      </div>
                    )}
                    {user.phone && (
                      <div className="flex items-center space-x-1 mt-1">
                        <Phone className={`h-3 w-3 ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
                        }`} />
                        <span>{user.phone}</span>
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 whitespace-nowrap text-right">
                  <span className={`inline-flex px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(user.is_active)}`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className={`px-3 sm:px-4 lg:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm hidden lg:table-cell ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  {new Date(user.created_at).toLocaleDateString()}
                </td>
                <td className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 whitespace-nowrap text-right text-sm font-medium w-12">
                  <div className="relative inline-block">
                    <button 
                      ref={(el) => { triggerRefs.current[user.id] = el; }}
                      className={`${
                        theme === 'dark' 
                          ? 'text-gray-400 hover:text-gray-300' 
                          : 'text-gray-400 hover:text-gray-600'
                      }`}
                      onClick={() => setOpenDropdownId(openDropdownId === user.id ? null : user.id)}
                    >
                      <MoreVertical className="h-4 w-4" />
                    </button>
                    {openDropdownId === user.id && (
                      <Dropdown
                        isOpen={true}
                        onClose={() => setOpenDropdownId(null)}
                        triggerRef={{ current: triggerRefs.current[user.id] } as any}
                        items={[
                          {
                            label: 'Edit',
                            icon: Edit,
                            onClick: () => {
                              onEdit(user);
                              setOpenDropdownId(null);
                            },
                            className: 'text-blue-600 hover:text-blue-900'
                          },
                          // Add More Details button for guests (including hosts-as-guests)
                          ...((user.role === 'guest' || user.roles?.includes('guest')) && onShowDetails ? [{
                            label: 'More Details',
                            icon: Info,
                            onClick: () => {
                              onShowDetails(user);
                              setOpenDropdownId(null);
                            },
                            className: 'text-blue-600 hover:text-blue-900'
                          }] : []),
                          {
                            label: user.is_active ? 'Deactivate' : 'Activate',
                            icon: user.is_active ? UserX : UserCheck,
                            onClick: () => {
                              onToggleStatus(user);
                              setOpenDropdownId(null);
                            },
                            className: user.is_active 
                              ? 'text-orange-600 hover:text-orange-900' 
                              : 'text-green-600 hover:text-green-900'
                          },
                          {
                            label: 'Delete',
                            icon: Trash2,
                            onClick: () => {
                              onDelete(user);
                              setOpenDropdownId(null);
                            },
                            className: 'text-red-600 hover:text-red-900'
                          }
                        ]}
                      />
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserTableView;
